import React, { Fragment, useEffect, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XIcon } from '@heroicons/react/outline'
import { useForm, Controller } from 'react-hook-form'
import { Button } from '../../../../components/Button'
import { SubmitButton } from './SubmitButton'
import { NCMSearchInput } from '../NCMSearchInput'
import { CreateNCMForm } from '../form/CreateNCMForm'
import { BasicProductFields } from '../form/BasicProductFields'
import { removePriceMask } from '../../utils/formatters'
import { UNIDADE_COMERCIAL } from '../../constants/tax-constants'
import { getDefaultNCMFormValues, prepareNCMDataForAPI } from '../../utils/ncm-helpers'
import { useFormPersistence } from '../../hooks/useFormPersistence'

export const ConfigureProductModal = ({ 
  isOpen, 
  onClose, 
  sku, 
  cost,
  onConfirm, 
  isLoading = false 
}) => {

  const [showCreateNCMForm, setShowCreateNCMForm] = useState(false)

  const formMethods = useForm({
    defaultValues: {
      sku: '',
      name: '',
      price: '',
      bar_code: '',
      ncm: '',
      origem: '',
      chave_fci: '',
      ...getDefaultNCMFormValues()
    },
    mode: 'onChange'
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
    trigger,
    control
  } = formMethods

  // Enable form persistence only in development
  const isDevelopment = process.env.NODE_ENV === 'development'
  const { clearPersistedData } = useFormPersistence(
    formMethods, 
    `configure-product-${sku}`, 
    isDevelopment
  )

  useEffect(() => {
    if (sku) {
      setValue('sku', sku)
    }
  }, [sku, setValue])

  useEffect(() => {
    if (isOpen) {
      // Only reset if not in development mode or if no persisted data exists
      if (!isDevelopment) {
        reset({
          sku: sku || '',
          name: '',
          price: '',
          bar_code: '',
          ncm: '',
          origem: '',
          chave_fci: '',
          ...getDefaultNCMFormValues()
        })
      } else {
        // In development, just ensure SKU is set
        setValue('sku', sku || '')
      }
    }
  }, [isOpen, sku, reset, setValue, isDevelopment])

  const handleModalClose = () => {
    reset()
    clearPersistedData()
    onClose()
  }

  const onSubmit = async (data) => {
    const isValid = await trigger()
    
    if (!isValid) {
      return
    }

    try {
      let productData = {
        product: {
          SKU: sku,
          NOME: data.name,
          EAN: data.bar_code,
          NCM: data.ncm,
          Origem: data.origin,
          Chave_FCI: data.fci_key ? data.fci_key.replace(/-/g, '') : '',
          Preco_Custo: parseFloat(removePriceMask(String(cost))),
          UNIDADA_COMERCIAL: UNIDADE_COMERCIAL,
          EXTIPI: "",
          REGRA_TRIBUTARIA: data.ncm,
        }
      }

      if (showCreateNCMForm && data.ncm_code && data.ncm_description) {
        const ncmData = prepareNCMDataForAPI(data)
        
        productData.product.NCM = ncmData.code
        productData.product.EXTIPI = ncmData.exception_code
        productData.product.REGRA_TRIBUTARIA = ncmData.exception_code ? `${ncmData.code}.${ncmData.exception_code}`: ncmData.code
        productData = {
          ...productData,
          tax_configuration: { ...ncmData }
        }

      }
      
      clearPersistedData()
      onConfirm(productData)
    } catch (error) {
      console.error('Erro ao criar NCM:', error)
      alert('❌ Erro ao criar NCM. Tente novamente.')
    }
  }

  const handleClose = () => {
    setShowCreateNCMForm(false)
    handleModalClose()
  }

  const handleRequestCreateNCM = () => {
    setShowCreateNCMForm(true)
  }

  const handleBackToNCMSearch = () => {
    setShowCreateNCMForm(false)
  }

  const handleClearNCMCode = () => {
    setValue('ncm_code', '')
    setValue('ncm_description', '')
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => {}}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center overflow-visible">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className={`w-full ${showCreateNCMForm ? 'max-w-5xl' : 'max-w-3xl'} transform overflow-visible rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all`}>
                <div className="flex justify-between items-start mb-6">
                  <Dialog.Title as="h3" className="text-xl font-semibold leading-6 text-gray-900">
                    Configurar Produto
                  </Dialog.Title>
                  <button
                    onClick={handleClose}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                    disabled={isLoading}
                  >
                    <XIcon className="h-6 w-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="space-y-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-blue-800 text-sm mb-0">
                        <strong>Produto não encontrado:</strong> O SKU "{sku}" não existe no sistema. 
                        Preencha as informações abaixo para criar este produto.
                      </p>
                    </div>

                    {/* Development persistence notice */}
                    {isDevelopment && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                        <div className="flex justify-between items-center">
                          <p className="text-yellow-800 text-xs mb-0">
                            🔧 <strong>Modo desenvolvimento:</strong> Os dados do formulário são salvos automaticamente 
                            durante o desenvolvimento para evitar perda de dados.
                          </p>
                          <button
                            type="button"
                            onClick={() => {
                              clearPersistedData()
                              reset({
                                sku: sku || '',
                                name: '',
                                price: '',
                                bar_code: '',
                                ncm: '',
                                origem: '',
                                chave_fci: '',
                                ...getDefaultNCMFormValues()
                              })
                            }}
                            className="text-yellow-600 hover:text-yellow-800 text-xs underline"
                          >
                            Limpar dados salvos
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Basic product fields */}
                    <BasicProductFields 
                      register={register}
                      errors={errors}
                      watch={watch}
                      setValue={setValue}
                      sku={sku}
                      disabled={isLoading}
                    />

                    {/* NCM field - only shows if not creating a new NCM */}
                    {!showCreateNCMForm && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          NCM (Nomenclatura Comum do Mercosul) *
                        </label>
                        <Controller
                          name="ncm"
                          control={control}
                          rules={{ required: 'NCM é obrigatório' }}
                          render={({ field: { onChange, value } }) => (
                            <NCMSearchInput
                              value={value}
                              setValue={setValue}
                              onChange={onChange}
                              error={errors.ncm}
                              placeholder="Digite código ou descrição do NCM"
                              onRequestCreateNCM={handleRequestCreateNCM}
                              onClearNCMCode={handleClearNCMCode}
                            />
                          )}
                        />
                        {errors.ncm && (
                          <p className="mt-1 mb-0 text-sm text-red-600">{errors.ncm.message}</p>
                        )}
                      </div>
                    )}

                    {/* Section to create new NCM */}
                    {showCreateNCMForm && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 relative">
                        <div className="mb-4">
                          <h4 className="text-lg font-semibold text-amber-800 mb-2">📝 Cadastrar Novo NCM</h4>
                          <p className="text-sm text-amber-700">
                            Preencha os dados tributários abaixo para criar um novo NCM. Após criado, ele será automaticamente selecionado no campo NCM acima.
                          </p>
                        </div>
                        <CreateNCMForm 
                          register={register}
                          formErrors={errors}
                          isCreatingNCM={showCreateNCMForm}
                          watch={watch}
                          setValue={setValue}
                          onBackToSearch={handleBackToNCMSearch}
                        />
                      </div>
                    )}
                  </div>

                  <div className="mt-8 flex justify-end space-x-3">
                    <Button
                      onClick={handleClose}
                      type="secondary"
                      disabled={isLoading}
                    >
                      Cancelar
                    </Button>
                    <SubmitButton
                      buttonType="primary"
                      type="submit"
                      disabled={isLoading}
                      loading={isLoading}
                      loading_label="Criando produto..."
                    >
                      Criar Produto
                    </SubmitButton>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
} 